import { useState } from "react";
import { Mail, MapPin, Phone, Clock } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { api } from "@/lib/api";
import Reveal from "@/components/Reveal";

export default function Contact() {
  const { t } = useLanguage();
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [sent, setSent] = useState(false);
  const [sending, setSending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.contact(form);
      setSent(true);
    } catch {
      setSent(false);
    } finally {
      setSending(false);
    }
  };

  const info = [
    { icon: Phone, label: t.contact.phoneL, value: "+30 210 810 0000", href: "tel:+302108100000" },
    { icon: Mail, label: t.contact.emailL, value: "info@antalia-pharma.com", href: "mailto:info@antalia-pharma.com" },
    { icon: MapPin, label: t.contact.addrL, value: "30 Agias Irinis Str., Marousi 15126, Athens" },
    { icon: Clock, label: t.contact.hoursL, value: t.contact.hoursV },
  ];

  return (
    <div data-testid="contact-page" className="bg-white">
      <section className="relative overflow-hidden border-b border-navy/10 bg-surface pb-16 pt-28">
        <span aria-hidden className="text-ghost pointer-events-none absolute -bottom-8 end-0 select-none font-display text-[14vw] italic leading-none">
          {t.contact.title}
        </span>
        <div className="relative mx-auto max-w-7xl px-6">
          <Reveal><p className="label-luxe text-gold-dark">{t.contact.kicker}</p></Reveal>
          <Reveal delay={1}>
            <h1 className="mt-5 font-display text-5xl font-light tracking-tight text-navy sm:text-6xl">{t.contact.title}</h1>
          </Reveal>
          <Reveal delay={2}>
            <p className="mt-5 text-sm font-light text-navy/50">{t.contact.sub}</p>
          </Reveal>
        </div>
      </section>

      <section className="mx-auto grid max-w-7xl gap-16 px-6 py-20 lg:grid-cols-5">
        <div className="space-y-4 lg:col-span-2">
          {info.map(({ icon: Icon, label, value, href }, i) => (
            <Reveal key={i} delay={i} className="flex items-start gap-4 border border-navy/10 bg-white p-6">
              <Icon size={18} strokeWidth={1.25} className="mt-0.5 shrink-0 text-gold-dark" />
              <div>
                <p className="label-luxe text-navy/40">{label}</p>
                {href ? (
                  <a data-testid={`contact-info-${i}`} href={href} className="mt-2 block text-sm font-medium text-navy transition-colors hover:text-gold-dark">
                    {value}
                  </a>
                ) : (
                  <p data-testid={`contact-info-${i}`} className="mt-2 text-sm font-medium text-navy">{value}</p>
                )}
              </div>
            </Reveal>
          ))}
        </div>

        <Reveal delay={1} className="lg:col-span-3">
          {sent ? (
            <div data-testid="contact-success" className="flex h-full flex-col items-center justify-center border border-sage/40 bg-sage-light/40 p-16 text-center">
              <p className="font-display text-3xl font-light text-navy">{t.contact.ok}</p>
            </div>
          ) : (
            <form onSubmit={submit} data-testid="contact-form" className="space-y-6 border border-navy/10 bg-white p-8 lg:p-12">
              <div className="grid gap-6 sm:grid-cols-2">
                <div>
                  <label htmlFor="contact-name" className="label-luxe mb-2 block text-navy/50">{t.contact.name}</label>
                  <input
                    id="contact-name"
                    data-testid="contact-name-input"
                    required
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className="input-luxe"
                  />
                </div>
                <div>
                  <label htmlFor="contact-email" className="label-luxe mb-2 block text-navy/50">{t.contact.email}</label>
                  <input
                    id="contact-email"
                    type="email"
                    data-testid="contact-email-input"
                    required
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    className="input-luxe"
                  />
                </div>
              </div>
              <div>
                <label htmlFor="contact-message" className="label-luxe mb-2 block text-navy/50">{t.contact.msg}</label>
                <textarea
                  id="contact-message"
                  rows={6}
                  data-testid="contact-message-input"
                  required
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  className="input-luxe resize-none"
                />
              </div>
              <button type="submit" data-testid="contact-submit-button" disabled={sending} className="btn-primary-luxe disabled:opacity-50">
                {sending ? t.co.processing : t.contact.send}
              </button>
            </form>
          )}
        </Reveal>
      </section>
    </div>
  );
}
